import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../components/ui/sonner';
import DashboardLayout from '../components/DashboardLayout';
import {
  Truck, Wrench, Plus, X, Smartphone, Cpu, Tablet, Package,
  AlertTriangle, ArrowRight, Search, ChevronDown,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'in_shop', label: 'In Shop' },
  { value: 'retired', label: 'Retired' },
];

// Expiry status for a YYYY-MM-DD date: null | {level:'expired'|'soon', days}
const expiryStatus = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const days = Math.ceil((d - new Date()) / 86400000);
  if (days < 0) return { level: 'expired', days };
  if (days <= 30) return { level: 'soon', days };
  return null;
};

const ExpiryBadge = ({ dateStr, label }) => {
  const s = expiryStatus(dateStr);
  if (!s) return null;
  const cls = s.level === 'expired' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800';
  const text = s.level === 'expired' ? `${label} expired` : `${label} expires in ${s.days}d`;
  return (
    <span data-testid={`expiry-${label.toLowerCase()}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${cls}`}>
      {text}
    </span>
  );
};


const TYPE_META = {
  truck: { label: 'Truck', icon: Truck, color: 'text-[#002FA7]', bg: 'bg-blue-50' },
  trailer: { label: 'Trailer', icon: Package, color: 'text-[#FF9500]', bg: 'bg-orange-50' },
  gps: { label: 'GPS Device', icon: Cpu, color: 'text-[#34C759]', bg: 'bg-green-50' },
  eld: { label: 'ELD Device', icon: Cpu, color: 'text-purple-600', bg: 'bg-purple-50' },
  tablet: { label: 'Tablet', icon: Tablet, color: 'text-slate-600', bg: 'bg-slate-100' },
  other: { label: 'Other', icon: Smartphone, color: 'text-slate-600', bg: 'bg-slate-100' },
};

const STATUS_PILL = {
  active: 'bg-green-100 text-green-700',
  in_shop: 'bg-amber-100 text-amber-700',
  retired: 'bg-slate-200 text-slate-600',
};

const emptyForm = {
  asset_type: 'truck',
  label: '',
  asset_number: '',
  serial_number: '',
  make: '',
  model: '',
  year: '',
  status: 'active',
  current_odometer: 0,
  assigned_driver_id: '',
  notes: '',
  license_plate: '',
  vin: '',
  registration_expiry: '',
  insurance_expiry: '',
  imei_or_mac: '',
  provider: '',
  plan: '',
};

const AssetsManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [maintenanceDue, setMaintenanceDue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showNumbering, setShowNumbering] = useState(false);
  const [numbering, setNumbering] = useState({ enabled: false, schemes: {} });
  const [reviewOnly, setReviewOnly] = useState(false);

  useEffect(() => {
    if (!(user?.role === 'company_admin' || user?.role === 'super_admin')) return;
    axios.get(`${API}/assets/numbering-settings`, { withCredentials: true })
      .then((r) => setNumbering(r.data))
      .catch(() => {});
  }, [user?.role]);

  const saveNumbering = async () => {
    try {
      const r = await axios.put(`${API}/assets/numbering-settings`, {
        enabled: numbering.enabled,
        schemes: numbering.schemes,
      }, { withCredentials: true });
      setNumbering(r.data);
      toast.success(numbering.enabled ? 'Auto-numbering saved' : 'Auto-numbering off');
      setShowNumbering(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
  };

  const setScheme = (type, field, value) => {
    setNumbering((n) => ({
      ...n,
      schemes: { ...n.schemes, [type]: { ...(n.schemes[type] || {}), [field]: value } },
    }));
  };
  const [query, setQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isAdmin = user?.role === 'company_admin' || user?.role === 'super_admin';
  const canManageStatus = isAdmin || user?.role === 'dispatcher';

  const handleStatusChange = async (e, asset, newStatus) => {
    e.stopPropagation();
    if (newStatus === asset.status) return;
    try {
      await axios.patch(
        `${API}/assets/${asset.id}/status`,
        { status: newStatus },
        { withCredentials: true },
      );
      toast.success(`${asset.label} → ${newStatus.replace('_', ' ')}`);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update status');
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [aRes, dRes, mRes] = await Promise.all([
        axios.get(`${API}/assets`, { withCredentials: true }),
        axios.get(`${API}/drivers`, { withCredentials: true }),
        axios.get(`${API}/maintenance/due`, { withCredentials: true }),
      ]);
      setAssets(aRes.data);
      setDrivers(dRes.data.filter(d => d.status === 'active'));
      setMaintenanceDue(mRes.data);
    } catch (e) {
      console.error('Failed to fetch', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        year: form.year ? parseInt(form.year, 10) : null,
        current_odometer: form.current_odometer ? parseFloat(form.current_odometer) : 0,
        assigned_driver_id: form.assigned_driver_id || null,
      };
      // Strip empty strings to null where appropriate
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      await axios.post(`${API}/assets`, payload, { withCredentials: true });
      setShowForm(false);
      setForm(emptyForm);
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create asset');
    } finally {
      setSubmitting(false);
    }
  };

  const pendingReviewCount = assets.filter(a => a.pending_admin_review).length;
  const filtered = assets.filter(a => {
    if (reviewOnly && !a.pending_admin_review) return false;
    if (filterType !== 'all' && a.asset_type !== filterType) return false;
    if (filterStatus !== 'all' && a.status !== filterStatus) return false;
    if (query) {
      const q = query.toLowerCase();
      const hay = `${a.label} ${a.serial_number || ''} ${a.license_plate || ''} ${a.vin || ''} ${a.make || ''} ${a.model || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const typeCounts = assets.reduce((acc, a) => { acc[a.asset_type] = (acc[a.asset_type] || 0) + 1; return acc; }, {});

  if (loading) {
    return <DashboardLayout><div className="p-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading assets...</div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="assets-page">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>Assets</h1>
            <p className="text-base text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Trucks, trailers, devices & preventative maintenance.</p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
            <button
              data-testid="asset-numbering-button"
              onClick={() => setShowNumbering(true)}
              className="border border-slate-300 text-slate-700 px-4 py-2.5 rounded-md font-semibold hover:bg-slate-100 transition-all flex items-center gap-2"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              title="Turn on automatic sequential asset numbers"
            >
              <Cpu className="w-4 h-4" /> Numbering{numbering.enabled ? ' · On' : ''}
            </button>
            <button
              data-testid="backfill-assets-button"
              onClick={async () => {
                try {
                  const r = await axios.post(`${API}/assets/backfill-from-applications`, {}, { withCredentials: true });
                  toast.success(`Synced ${r.data?.synced_drivers || 0} driver application(s)`);
                  fetchAll();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Sync failed');
                }
              }}
              className="border border-slate-300 text-slate-700 px-4 py-2.5 rounded-md font-semibold hover:bg-slate-100 transition-all flex items-center gap-2"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              title="Pull truck/trailer info from submitted driver applications into Asset Management"
            >
              <Truck className="w-4 h-4" /> Sync from applications
            </button>
            <button
              data-testid="create-asset-button"
              onClick={() => setShowForm(true)}
              className="bg-[#002FA7] text-white px-4 py-2.5 rounded-md font-semibold hover:bg-[#00247D] transition-all flex items-center gap-2"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <Plus className="w-4 h-4" /> New Asset
            </button>
            </div>
          )}
        </div>

        {/* Maintenance Due Banner */}
        {maintenanceDue.length > 0 && (
          <div data-testid="maintenance-due-banner" className="mb-6 bg-amber-50 border border-amber-200 rounded-md p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-amber-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                  {maintenanceDue.length} maintenance {maintenanceDue.length === 1 ? 'item is' : 'items are'} due or overdue
                </p>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {maintenanceDue.slice(0, 6).map((m, idx) => (
                    <button
                      key={idx}
                      data-testid={`maint-due-item-${idx}`}
                      onClick={() => navigate(`/assets/${m.asset.id}`)}
                      className="text-left text-xs bg-white border border-amber-200 rounded px-2 py-1.5 hover:border-amber-400 transition-colors"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      <span className={`font-semibold ${m.status.is_overdue ? 'text-red-700' : 'text-amber-800'}`}>
                        {m.status.is_overdue ? 'OVERDUE' : 'DUE'}
                      </span>
                      <span className="text-slate-700 ml-2">{m.rule.name} — {m.asset.label}</span>
                      {m.status.miles_remaining !== null && (
                        <span className="text-slate-500 ml-1">({Math.abs(Math.round(m.status.miles_remaining))} mi {m.status.miles_remaining < 0 ? 'overdue' : 'left'})</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Type quick filter */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button
            data-testid="filter-type-all"
            onClick={() => setFilterType('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${filterType === 'all' ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'}`}
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            All ({assets.length})
          </button>
          {Object.keys(TYPE_META).map(t => (
            <button
              key={t}
              data-testid={`filter-type-${t}`}
              onClick={() => setFilterType(t)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${filterType === t ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'}`}
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {TYPE_META[t].label} ({typeCounts[t] || 0})
            </button>
          ))}
          {pendingReviewCount > 0 && (
            <button
              data-testid="filter-needs-review"
              onClick={() => setReviewOnly((v) => !v)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${reviewOnly ? 'bg-amber-500 text-white border-amber-500' : 'bg-amber-50 border-amber-300 text-amber-800 hover:border-amber-500'}`}
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              Needs review ({pendingReviewCount})
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <select
            data-testid="filter-status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 rounded-md border border-slate-200 text-sm bg-white"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="in_shop">In shop</option>
            <option value="retired">Retired</option>
          </select>
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="search-assets"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by label, plate, VIN, serial..."
              className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-200 text-sm bg-white focus:outline-none focus:border-[#002FA7]"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div data-testid="no-assets" className="bg-white border border-dashed border-slate-300 rounded-md p-12 text-center">
            <Wrench className="w-10 h-10 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {assets.length === 0 ? 'No assets yet. Create your first truck, trailer, or device.' : 'No assets match your filters.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(a => {
              const meta = TYPE_META[a.asset_type] || TYPE_META.other;
              const Icon = meta.icon;
              return (
                <div
                  key={a.id}
                  data-testid={`asset-card-${a.id}`}
                  onClick={() => navigate(`/assets/${a.id}`)}
                  className="bg-white border border-slate-200 rounded-md p-5 cursor-pointer hover:border-[#002FA7] hover:shadow-sm transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-11 h-11 ${meta.bg} rounded-md flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${meta.color}`} strokeWidth={2} />
                    </div>
                    {canManageStatus ? (
                      <div className="relative inline-block" onClick={(e) => e.stopPropagation()} data-testid={`asset-status-picker-${a.id}`}>
                        <select
                          value={a.status}
                          onChange={(e) => handleStatusChange(e, a, e.target.value)}
                          className={`appearance-none cursor-pointer pl-2 pr-6 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border-0 focus:outline-none focus:ring-2 focus:ring-[#002FA7] ${STATUS_PILL[a.status]}`}
                          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                          title="Change status"
                        >
                          {STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                        <ChevronDown className="w-3 h-3 absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none opacity-60" />
                      </div>
                    ) : (
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${STATUS_PILL[a.status]}`}>
                        {a.status === 'in_shop' ? 'In Shop' : a.status}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {meta.label}{a.year ? ` • ${a.year}` : ''}{a.asset_number ? ` • #${a.asset_number}` : ''}
                  </p>
                  {a.pending_admin_review && (
                    <span data-testid={`asset-needs-review-${a.id}`} className="inline-flex items-center gap-1 mb-1.5 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800">
                      Needs review · from driver
                    </span>
                  )}
                  <div className="flex flex-wrap gap-1.5 mb-1.5">
                    <ExpiryBadge dateStr={a.registration_expiry} label="Registration" />
                    <ExpiryBadge dateStr={a.insurance_expiry} label="Insurance" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1 truncate" style={{ fontFamily: 'Work Sans, sans-serif' }}>{a.label}</h3>
                  <p className="text-xs text-slate-600 mb-3 truncate" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {[a.make, a.model].filter(Boolean).join(' ') || a.serial_number || '—'}
                  </p>
                  <div className="space-y-1 text-xs" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {a.license_plate && (
                      <div className="flex justify-between"><span className="text-slate-500">Plate</span><span className="text-slate-800 font-medium">{a.license_plate}</span></div>
                    )}
                    {(a.current_odometer != null) && a.asset_type === 'truck' && (
                      <div className="flex justify-between"><span className="text-slate-500">Odometer</span><span className="text-slate-800 font-medium">{Math.round(a.current_odometer).toLocaleString()} mi</span></div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-slate-500">Driver</span>
                      <span className={`font-medium truncate ml-2 ${a.assigned_driver_name ? 'text-slate-800' : 'text-slate-400 italic'}`}>{a.assigned_driver_name || 'Unassigned'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-[#002FA7] text-xs font-semibold mt-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    View details <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create modal */}
      {showNumbering && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="numbering-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Auto Asset Numbers</h2>
              <button onClick={() => setShowNumbering(false)} data-testid="numbering-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            <label className="flex items-center gap-3 mb-4 cursor-pointer">
              <input type="checkbox" data-testid="numbering-enabled" checked={numbering.enabled} onChange={(e) => setNumbering({ ...numbering, enabled: e.target.checked })} className="w-4 h-4" />
              <span className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Automatically assign a number to every new asset</span>
            </label>
            <div className="space-y-2 mb-4 max-h-72 overflow-y-auto">
              <div className="grid grid-cols-12 gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 px-1">
                <span className="col-span-4">Type</span><span className="col-span-4">Prefix</span><span className="col-span-4">Next #</span>
              </div>
              {['truck', 'trailer', 'gps_device', 'eld_device', 'tablet', 'other'].map((t) => {
                const s = numbering.schemes?.[t] || {};
                const labelMap = { truck: 'Truck', trailer: 'Trailer', gps_device: 'GPS Device', eld_device: 'ELD Device', tablet: 'Tablet', other: 'Other' };
                return (
                  <div key={t} className="grid grid-cols-12 gap-2 items-center" data-testid={`numbering-row-${t}`}>
                    <span className="col-span-4 text-sm text-slate-700">{labelMap[t]}</span>
                    <input data-testid={`numbering-prefix-${t}`} value={s.prefix ?? ''} onChange={(e) => setScheme(t, 'prefix', e.target.value)} disabled={!numbering.enabled} className="col-span-4 px-2.5 py-1.5 text-sm border border-slate-300 rounded-md disabled:bg-slate-50" />
                    <input data-testid={`numbering-next-${t}`} type="number" value={s.next_seq ?? ''} onChange={(e) => setScheme(t, 'next_seq', parseInt(e.target.value, 10) || 0)} disabled={!numbering.enabled} className="col-span-4 px-2.5 py-1.5 text-sm border border-slate-300 rounded-md disabled:bg-slate-50" />
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-500 mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Each type counts up on its own (e.g. Trucks <span className="font-bold text-slate-700">{numbering.schemes?.truck?.prefix}{numbering.schemes?.truck?.next_seq}</span>, Trailers <span className="font-bold text-slate-700">{numbering.schemes?.trailer?.prefix}{numbering.schemes?.trailer?.next_seq}</span>). Manually-typed numbers always win.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowNumbering(false)} className="px-4 py-2 rounded-md text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-100">Cancel</button>
              <button onClick={saveNumbering} data-testid="numbering-save" className="px-4 py-2 rounded-md text-sm font-semibold bg-[#002FA7] text-white hover:bg-[#00247D]">Save</button>
            </div>
          </div>
        </div>
      )}


      {showForm && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4" data-testid="asset-form-modal">
          <div className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>New Asset</h2>
              <button data-testid="close-asset-form" onClick={() => { setShowForm(false); setForm(emptyForm); }} className="p-1.5 rounded-md hover:bg-slate-100">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Asset Type *</label>
                  <select
                    data-testid="asset-type-select"
                    value={form.asset_type}
                    onChange={(e) => setForm({ ...form, asset_type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                  >
                    {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Label / Name *</label>
                  <input
                    data-testid="asset-label-input"
                    type="text"
                    required
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    placeholder="e.g. Unit 102, Trailer A, ELD #5"
                    className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Asset Number</label>
                  <input
                    data-testid="asset-number-input"
                    type="text"
                    value={form.asset_number}
                    onChange={(e) => setForm({ ...form, asset_number: e.target.value })}
                    placeholder="e.g. T-104"
                    className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Serial Number / VIN</label>
                  <input data-testid="asset-serial-input" type="text" value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Status</label>
                  <select data-testid="asset-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm">
                    <option value="active">Active</option>
                    <option value="in_shop">In Shop</option>
                    <option value="retired">Retired</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Make</label>
                  <input data-testid="asset-make-input" type="text" value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Model</label>
                  <input data-testid="asset-model-input" type="text" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Year</label>
                  <input data-testid="asset-year-input" type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Current Odometer (mi)</label>
                  <input data-testid="asset-current-odometer-input" type="number" min="0" value={form.current_odometer} onChange={(e) => setForm({ ...form, current_odometer: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Assign to Driver</label>
                  <select
                    data-testid="asset-assigned-driver-select"
                    value={form.assigned_driver_id}
                    onChange={(e) => setForm({ ...form, assigned_driver_id: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                  >
                    <option value="">Unassigned</option>
                    {drivers.map(d => <option key={d.user_id} value={d.user_id}>{d.name || d.email}</option>)}
                  </select>
                </div>

                {(form.asset_type === 'truck' || form.asset_type === 'trailer') && (
                  <>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>License Plate</label>
                      <input data-testid="asset-license-plate-input" type="text" value={form.license_plate} onChange={(e) => setForm({ ...form, license_plate: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>VIN</label>
                      <input data-testid="asset-vin-input" type="text" value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Registration Expiry</label>
                      <input data-testid="asset-registration-expiry-input" type="date" value={form.registration_expiry} onChange={(e) => setForm({ ...form, registration_expiry: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Insurance Expiry</label>
                      <input data-testid="asset-insurance-expiry-input" type="date" value={form.insurance_expiry} onChange={(e) => setForm({ ...form, insurance_expiry: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                    </div>
                  </>
                )}

                {(form.asset_type === 'gps' || form.asset_type === 'eld' || form.asset_type === 'tablet') && (
                  <>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>IMEI / MAC</label>
                      <input data-testid="asset-imei-input" type="text" value={form.imei_or_mac} onChange={(e) => setForm({ ...form, imei_or_mac: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Provider</label>
                      <input data-testid="asset-provider-input" type="text" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="Verizon, Geotab, Samsara..." className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Plan</label>
                      <input data-testid="asset-plan-input" type="text" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                    </div>
                  </>
                )}

                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Notes</label>
                  <textarea data-testid="asset-notes-input" rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
                <button type="button" onClick={() => { setShowForm(false); setForm(emptyForm); }} className="px-4 py-2 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-100" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Cancel</button>
                <button data-testid="asset-create-submit" type="submit" disabled={submitting} className="px-5 py-2 bg-[#002FA7] text-white rounded-md text-sm font-semibold hover:bg-[#00247D] transition-all disabled:opacity-50" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {submitting ? 'Creating...' : 'Create Asset'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default AssetsManagement;
